<template>
  <div class="member-search">

    <create-invitation-dialog
        v-if="organization && canOnboard"
        v-model="creatingInvitation"
        :organization-id="organization.id"
        @sent="$refs.pendingInvitations && $refs.pendingInvitations.load()"
    />

    <!-- Add button -->
    <v-row>
      <v-col class="text-right">
        <!--
          Nouvel onboarding : ce qui attend (les demandes à valider en rouge) et le lien de partage, dans une fenêtre,
          pour que la liste des membres reste en haut (le reste de l'onglet est l'invitation V1, inchangée).
        -->
        <dialog-button
            v-if="organization && (canOnboard || canApprove)"
            :label="$t('onboarding.members.pending.button')"
            :title="$t('onboarding.members.pending.title')"
            icon="mdi-email-fast-outline"
            :count="invitations.awaitingApproval || invitations.total"
            :alert="invitations.awaitingApproval > 0"
            class="mr-3"
        >
          <v-row>
            <v-col cols="12" lg="8">
              <pending-invitations ref="pendingInvitations" :organization-id="organization.id" embedded @create="creatingInvitation = true" @counts="invitations = $event" />
            </v-col>
            <v-col cols="12" lg="4">
              <invitation-link-card :organization-id="organization.id" />
            </v-col>
          </v-row>
        </dialog-button>
        <v-btn class="mr-3" small @click="openAddCustomer()" v-if="currentUser.isZE">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('customer.add-customer') }}
        </v-btn>
        <v-btn small @click="openInviteMember()">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('member.invite-member') }}
        </v-btn>
        <v-menu left offset-y>
          <template #activator="{ on, attrs }">
            <v-btn
                v-bind="attrs"
                v-on="on"
                class="ml-3"
                small
            >
              <v-icon>mdi-cog</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item :to="{name: 'organizations.members.types'}">
              <v-list-item-title>{{ $t('member-type.manage') }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.members.groups'}">
              <v-list-item-title>{{ $t('member-group.manage') }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.members.invitations'}">
              <v-list-item-title>{{ $t('member.invitation.manage') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Search -->
    <v-row>
      <v-col>
        <search-field
            :placeholder="$t('member.search-member')"
            :search.sync="search"
            :options.sync="options"
            clearable
        >
        </search-field>
        <filter-by-letter
            :selectedLetter="startBy"
            @update:startBy="updateStartBy"
        />
        <v-switch
            v-model="showUnregistered"
            :label="$t('member.show-unregistered')"
            dense
            hide-details
            class="mt-2"
            @change="loadData('search')"
        />
      </v-col>
    </v-row>


    <!-- List -->
    <v-row no-gutters class="mt-10">
      <v-col>
        <v-data-table
            class="member-list"
            :headers="headers"
            :items="items"
            :loading="loading"
            :options.sync="options"
            :server-items-length="total"
            @click:row="row => $router.push({ name: 'member', params: {memberId: row.id} })"
            :footer-props="{
              'items-per-page-options': [
                  20, 50, 100
              ]
            }"
        >
          <template #item.customer.evcoId="{ item, value }">
            <router-link v-if="value" class="click" :to="{ name: 'member', params: { memberId: item.id } }" @click.native.stop>
              {{ value }}
            </router-link>
            <template v-else>-</template>
          </template>

          <template #item.type.name="{ item }">
            <div class="d-flex align-center text-no-wrap">
              <span>{{ item.type ? item.type.name : '' }}</span>
              <v-tooltip bottom v-if="(item.missingRequiredCustomFields || []).length">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="warning" class="ml-1" v-bind="attrs" v-on="on" @click.stop>mdi-alert</v-icon>
                </template>
                <span>{{ $t('member.missing-required-custom-fields', { fields: item.missingRequiredCustomFields.map(f => f.label).join(', ') }) }}</span>
              </v-tooltip>
            </div>
          </template>

          <template #item.status="{ item }">
            <v-tooltip v-if="item.unregisteredAt" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip small color="grey" text-color="white" v-bind="attrs" v-on="on">
                  {{ $t('member.unregistered') }}
                </v-chip>
              </template>
              <span>{{ $t('member.unregistered-at') }} {{ item.unregisteredAt | formatDate('DD/MM/YYYY [à] HH[:]mm[:]ss') }}</span>
            </v-tooltip>
            <template v-else>{{ $t('member.status.' + item.status) }}</template>
          </template>

          <template #item.homeChargingPointsCount="{ value }">
            {{ value > 0 ? $t('yes') : $t('no') }}
          </template>

<!--          <template #item.invitedAt="{ value }">-->
<!--            {{ value|formatDate }}-->
<!--          </template>-->

          <template #item.createdAt="{ value }">
            {{ value|formatDate }}
          </template>

          <template #item.groupsCount="{ item }">
            <member-group-type :item="group" :key="group.id"  v-for="group in item.groups"></member-group-type>
          </template>

          <template #item.tokensCount="{ value }">
            {{ value }}
          </template>

          <template #item.defaultVehicleRegistrationNumber="{ item }">
            {{ memberDefaultPlate(item) || '-' }}
          </template>

          <template #item.actions="{ item }">

            <!-- Unregister (soft-delete): only for still-registered members -->
            <v-btn v-if="!item.unregisteredAt" icon @click.stop="deleteMember(item)" :title="$t('member.unregister')">
              <v-icon small>mdi-account-off</v-icon>
            </v-btn>
            <!-- Re-register: only for former members -->
            <v-btn v-else icon @click.stop="openReregister(item)" :title="$t('member.reregister')">
              <v-icon small>mdi-account-plus</v-icon>
            </v-btn>
          </template>

        </v-data-table>

      </v-col>
    </v-row>

    <v-row class="mb-5">
      <v-col>
        <v-btn small @click="downloadCsv" :loading="loadingExport" :disabled="loadingExport" type="button">
          <v-icon left>mdi-file-delimited-outline</v-icon>
          {{ $t("export") }}
        </v-btn>
      </v-col>
    </v-row>



    <!-- Modal: réinscription sur une période différente (annuler vs nouvelle inscription) -->
    <v-dialog v-model="reregisterDialog.show" max-width="520">
      <v-card>
        <v-card-title>{{ $t('member.reregister') }}</v-card-title>
        <v-card-text>{{ $t('member.reregister-different-period') }}</v-card-text>
        <v-card-actions>
          <v-btn text @click="reregisterDialog.show = false">{{ $t('cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmCancelUnregistration()">{{ $t('member.reregister-cancel-option') }}</v-btn>
          <v-btn color="primary" @click="confirmNewRegistration()">{{ $t('member.reregister-new-option') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: Invitation membre -->
    <invite-member-modal
        v-if="organization"
        ref="inviteMember"
        :organization="organization"
        @invitationSent="loadData('modal event')"
    ></invite-member-modal>

    <v-dialog v-model="addCustomerModal" max-width="600" v-if="addCustomerModal">
      <v-form @submit.prevent="addFromCustomer">
        <v-card>
          <v-card-title>
            {{ $t('customer.add-customer')}}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="my-6">
            <v-row>
              <v-col>
                <customer-select
                    v-model="customerIdToAdd"
                    :label="$tc('customer.customer')"
                    :placeholder="$t('customer.search-placeholder')"
                    clearable
                ></customer-select>
              </v-col>
            </v-row>

          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-row class="ma-0">
              <v-col class="text-right">
                <v-btn small @click="addCustomerModal = false">{{ $t('cancel') }}</v-btn>
                <v-btn small class="ml-3" color="primary" type="submit" :disabled="!customerIdToAdd">{{ $t('add') }}</v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

  </div>
</template>

<script>
import MemberRepository from "@repository/MemberRepository";
import InviteMemberModal from "@/components/modals/InviteMemberModal.vue";
import MemberGroupType from "@blocks/MemberGroupType.vue";
import CustomerSelect from "@blocks/Select/CustomerSelect.vue";
import FilterByLetter from "@blocks/FilterByLetter.vue";
import PendingInvitations from "@pages/Onboarding/Members/PendingInvitations.vue";
import DialogButton from "@/components/shared/DialogButton.vue";
import CreateInvitationDialog from "@pages/Onboarding/Members/CreateInvitationDialog.vue";
import InvitationLinkCard from "@pages/Onboarding/Members/InvitationLinkCard.vue";

export default {
  name: 'OrganizationMembers',
  components: {
    DialogButton,
    PendingInvitations,
    CreateInvitationDialog,
    InvitationLinkCard,
    FilterByLetter,
    MemberGroupType,
    InviteMemberModal,
    CustomerSelect,
  },

  data() {
    return {
      creatingInvitation: false,
      invitations: { total: 0, awaitingApproval: 0 },
      addCustomerModal: false,
      inviteMemberModal: {
        displayed: true
      },
      search: '',
      customerIdToAdd: null,
      loading: false,
      loadingExport: false,
      selectedItem: null,
      items: [],
      options: null,
      startBy: null,
      showUnregistered: false,
      reregisterDialog: { show: false, member: null },
      total: 0,
      headers: [
        {text: this.$t('customer.evco-id'), value: 'customer.evcoId'},
        {text: this.$t('lastname'), value: 'lastname'},
        {text: this.$t('firstname'), value: 'firstname'},
        {text: this.$t('member.external-reference'), value: 'externalReference'},
        {text: this.$t('type'), value: 'type.name'},
        {text: this.$tc('group', 2), value: 'groupsCount'},
        {text: this.$tc('member.home-charging-point', 2), value: 'homeChargingPointsCount'},
        {text: this.$tc('member.token', 2), value: 'tokensCount'},
        {text: this.$t('vehicle-registration'), value: 'defaultVehicleRegistrationNumber', sortable: false},
        {text: this.$t('status'), value: 'status'},
        // {text: this.$t('member.invited-at'), value: 'invitedAt'},
        {text: this.$t('member.joined-at'), value: 'createdAt'},
        {text: '', value: 'actions', sortable: false},
      ],
    }
  },

  computed: {
    canOnboard() {
      const permissions = this.currentUser?.permissions || []
      return permissions.includes('ONBOARDING_INVITE') || permissions.includes('ONBOARDING_MODULE_MANAGE')
    },
    canApprove() {
      return (this.currentUser?.permissions || []).includes('MEMBER_APPROVE')
    },
    organization() {
      return this.$parent.organization ?? null
    }
  },

  watch: {
    startBy() {
      this.loadData('search');
    },
    search() {
      if(this.options.page > 1) {
        this.options.page = 1
      } else {
        this.loadData('search')
      }
    },

    options() {
      this.loadData('options')
    },

    selectedItem(val) {
      this.$router.push({name: 'organizations.edit', params: {id: val.id} })
    },
  },

  methods: {
    updateStartBy(letter) {
      this.startBy = letter;
    },
    memberDefaultPlate(member) {
      if (!member || !member.defaultVehicleId || !Array.isArray(member.vehicles)) return null
      const found = member.vehicles.find(v => v.id === member.defaultVehicleId)
      return found ? found.registrationNumber : null
    },
    loadData(source) {
      this.loading = true

      let data = {
        searchText: this.search,
      }
      if (this.options.itemsPerPage > -1) {
        data.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
      }
      data.limit = this.options.itemsPerPage

      data.organizationId = this.organization.id

      if (this.options.sortBy.length > 0) {
        data.orderBy = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0) {
        data.orderDirection = this.options.sortDesc[0] ? 'desc' : 'asc'
      }
      data.startBy = this.startBy
      data.includeUnregistered = this.showUnregistered
      MemberRepository.search(data).then(results => {
        this.total = results.total
        this.items = results.items.map(item => {
          item.groups = item.groups.toSorted(function(a, b) {
            let x = a.name?.toLowerCase(); let y = b.name?.toLowerCase();
            return x < y ? -1 : (x > y ? 1 : 0)
          })
          return item
        })
        this.loading = false
      }).catch(e => {
        this.notifyError(e)
        this.loading = false
      })
    },

    openAddCustomer() {
      this.customerIdToAdd = null
      this.addCustomerModal = true
    },

    openInviteMember() {
      this.$refs.inviteMember.open()
    },

    isSameMonthAsNow(dateStr) {
      if (!dateStr) return false
      const u = new Date(dateStr)
      const n = new Date()
      return u.getFullYear() === n.getFullYear() && u.getMonth() === n.getMonth()
    },
    openReregister(item) {
      // Same period (month) as the unregistration → no ambiguity, just cancel it.
      // Different period → ask: cancel the unregistration or create a new registration.
      if (this.isSameMonthAsNow(item.unregisteredAt)) {
        this.$dialog.confirm({text: this.$t('member.reregister-confirm')}).then(result => {
          if (result) this.doCancelUnregistration(item)
        })
      } else {
        this.reregisterDialog = {show: true, member: item}
      }
    },
    confirmCancelUnregistration() {
      const member = this.reregisterDialog.member
      this.reregisterDialog.show = false
      this.doCancelUnregistration(member)
    },
    confirmNewRegistration() {
      const member = this.reregisterDialog.member
      this.reregisterDialog.show = false
      this.doReregister(member)
    },
    doCancelUnregistration(item) {
      this.showLoading(true)
      MemberRepository.cancelMemberUnregistration(item.id).then(() => {
        this.loadData()
        this.showLoading(false)
      }).catch(e => {
        this.showLoading(false)
        this.notifyError(e)
      })
    },
    doReregister(item) {
      this.showLoading(true)
      MemberRepository.reregisterMember(item.id).then(() => {
        this.loadData()
        this.showLoading(false)
      }).catch(e => {
        this.showLoading(false)
        this.notifyError(e)
      })
    },

    deleteMember(item) {
      this.$dialog.confirm({text: this.$t('member.unregister-confirm')}).then(result => {
        if (result) {
          this.showLoading(true)
          MemberRepository.deleteMember(item.id).then(() => {
            this.loadData()
            this.showLoading(false)
          }).catch(e => {
            this.showLoading(false)
            this.notifyError(e)
          })
        }
      })
    },

    addFromCustomer() {
      if(this.customerIdToAdd) {
        this.showLoading(true)
        MemberRepository.createFromCustomer(this.organization.id, this.customerIdToAdd).then(() => {
          this.addCustomerModal = false
          this.loadData()
          this.showLoading(false)
        }).catch(e => {
          this.showLoading(false)
          this.notifyError(e)
        })
      }
    },

    downloadCsv() {
      this.loadingExport = true
      MemberRepository.exportCsv(this.organization.id).then(() => {
        this.addCustomerModal = false
        this.loadingExport = false
      }).catch(e => {
        this.loadingExport = false
        this.notifyError(e)
      })
    },

  },
}
</script>

<style lang="scss">
  .member-search {
    min-height: 1000px;
    .member-list {
      td, th {
        cursor: pointer;
        &.text-start {
          text-align: left !important;
        }
      }
      .click {
        text-decoration: none;
        color: #71A723;
        font-weight: bold;
      }
    }
  }


</style>
